import React, { useState, useEffect, useMemo, useCallback } from "react";
import { notification } from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation, useOutletContext } from "react-router-dom";
import { API_BASE_URL } from "../../constants/config";
import { TransferTable } from "./TransferTable";
import { ErrorFallback } from "./components/ErrorFallback";
import { Transfer } from "./types/wallet";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { getAndSanitize } from "../../shared/utils/sanitization";
import { useEffectiveAdminId } from "../../contexts/AdminSelectorContext";

type ExcludesFalsy = <T>(x: T | null | undefined | false | 0 | "") => x is T;

interface PaginatedResponse {
  success: boolean;
  data: Transfer[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface WalletSearchContext {
  searchTerm: string;
  filterPlatform: string | null;
  onSearchChange: (value: string) => void;
  onPlatformFilterChange: (platform: string | null) => void;
  onRefreshTrigger?: () => void;
  platforms?: string[];
}

export const Wallet: React.FC = React.memo(() => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const effectiveAdminId = useEffectiveAdminId();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [filterPlatform, setFilterPlatform] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const { t } = useTranslation();

  // Get search context from outlet context
  const context = useOutletContext<{
    walletSearchParams?: { searchTerm: string; filterPlatform: string | null };
    walletRefreshTrigger?: number;
    onWalletPlatformsUpdate?: (platforms: string[]) => void;
  }>();

  // Use search params from parent context if available
  const effectiveSearchTerm = context?.walletSearchParams?.searchTerm ?? searchTerm;
  const effectiveFilterPlatform = context?.walletSearchParams?.filterPlatform ?? filterPlatform;

  // Sync local state with parent context
  useEffect(() => {
    if (context?.walletSearchParams) {
      setSearchTerm(context.walletSearchParams.searchTerm);
      setFilterPlatform(context.walletSearchParams.filterPlatform);
    }
  }, [context?.walletSearchParams]);

  // Debounce search for better UX
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(effectiveSearchTerm);
      setPagination((prev) => ({ ...prev, current: 1 })); // Reset to first page on search
    }, 300);

    return () => clearTimeout(handler);
  }, [effectiveSearchTerm]);

  // Query configuration
  const {
    data: transfersResponse = {
      data: [],
      total: 0,
      page: 1,
      limit: 10,
      hasMore: false,
    },
    isLoading: isLoadingTransfers,
    isError: isErrorTransfers,
    refetch: refetchTransfers,
  } = useQuery<PaginatedResponse>({
    queryKey: [
      "transfers",
      effectiveAdminId,
      pagination.current,
      pagination.pageSize,
      debouncedSearchTerm,
      effectiveFilterPlatform,
    ],
    queryFn: async () => {
      try {

        // Obtain token and verify existence
        const token = getAndSanitize("token");

        if (!token) {
          throw new Error("No token available");
        }

        const params: Record<string, string | number> = {
          page: pagination.current,
          limit: pagination.pageSize,
        };

        // Send panelId from admin selector (null = all, string = specific admin)
        if (effectiveAdminId) {
          params.panelId = effectiveAdminId;
        }

        if (debouncedSearchTerm) params.searchTerm = debouncedSearchTerm;
        if (effectiveFilterPlatform) params.platform = effectiveFilterPlatform;

        const response = await axios.get<PaginatedResponse>(
          `${API_BASE_URL}/api/transfers/list`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Cache-Control": "no-cache",
            },
            params,
          }
        );


        return response.data;
      } catch (error) {
        console.error("[Wallet] Error al obtener transferencias:", error);
        if (axios.isAxiosError(error)) {
          console.error("[Wallet] Detalles del error:", {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
          });
        }
        throw error;
      }
    },
    // Always enabled - effectiveAdminId null means show all (SUPERADMIN)
    enabled: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    refetchOnMount: true,
  });

  const handleRefresh = useCallback(async (): Promise<void> => {
    try {
      try {
        // Try with new route first
        await axios.post(
          `${API_BASE_URL}/api/transfers/sync-all`,
          {},
          {
            headers: {
              Authorization: `Bearer ${getAndSanitize("token")}`,
            },
          }
        );
      } catch (syncError) {
        console.error(
          "[Wallet] Error con /sync-all, intentando ruta alternativa:",
          syncError
        );
        // If it fails, try with old route
        await axios.post(
          `${API_BASE_URL}/api/transfers/sync-all-transfers`,
          {},
          {
            headers: {
              Authorization: `Bearer ${getAndSanitize("token")}`,
            },
          }
        );
      }

      await refetchTransfers();

      notification.success({
        message: t("wallet.notifications.refreshSuccess"),
        duration: 3,
      });
    } catch (error) {
      console.error(
        "[Wallet] Refresh error:",
        error instanceof Error ? error.message : error
      );
      notification.error({
        message: t("wallet.notifications.refreshError"),
        description:
          error instanceof Error
            ? error.message
            : t("wallet.notifications.unknownError"),
        duration: 3,
      });
    }
  }, [queryClient, t]);

  const handlePaginationChange = useCallback(
    (page: number, pageSize?: number) => {
      setPagination({
        current: page,
        pageSize: pageSize || pagination.pageSize,
      });
    },
    [pagination.pageSize]
  );

  const platforms = useMemo(
    () =>
      Array.from(
        new Set(
          transfersResponse.data
            .map((t) => t.platform)
            .filter(Boolean as unknown as ExcludesFalsy)
        )
      ),
    [transfersResponse.data]
  );

  // Sync platforms with parent context
  useEffect(() => {
    if (context?.onWalletPlatformsUpdate && platforms.length > 0) {
      context.onWalletPlatformsUpdate(platforms);
    }
  }, [platforms, context]);

  // Trigger refresh when parent requests it
  useEffect(() => {
    if (context?.walletRefreshTrigger && context.walletRefreshTrigger > 0) {
      handleRefresh();
    }
  }, [context?.walletRefreshTrigger]);

  useEffect(() => {
    return () => {
      if (!location.pathname.includes("/user-panel")) {
        queryClient.invalidateQueries({ queryKey: ["transfers"] });
        queryClient.invalidateQueries({ queryKey: ["request-details"] });
      }
    };
  }, [queryClient, location.pathname]);

  const isLoading = isLoadingTransfers;

  return (
    <div>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={handleRefresh}
        onError={(error) => {
          console.error("[Wallet] Error boundary caught error:", error);
        }}
      >
        <div style={{
  marginTop: '16px',
  marginBottom: isTabletOrMobile ? '200px' : '500px',
  justifyContent: 'center',
  width: '100%'
}}>
  <TransferTable
    transfers={transfersResponse.data}
    isLoading={isLoading}
    isError={isErrorTransfers}
    onRefresh={handleRefresh}
    pagination={{
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: transfersResponse.total || 0,
      onChange: handlePaginationChange,
      showSizeChanger: true,
      showTotal: (total) =>
        `${t("wallet.pagination.total")}: ${total} ${t(
          "wallet.pagination.transfers"
        )}`,
      // Remove all non-supported properties: responsive, size, position, style
    }}
  />
</div>

      </ErrorBoundary>
    </div>
  );
});

export default Wallet;